import { useHubFrame } from '../hub/HubFrameContext';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { useBottomBarInset } from '../consent/bottomBarInset';
import { useGuidesIndexHref } from '../hub/giftGuideHubFlag';

/**
 * Bottom tab bar for the mobile web shell.
 *
 * Four slots: People (anchor to /#your-people), Calendar (/calendar),
 * Gift Guides (/gift-guides — the per-occasion guide index), and You
 * (profile/account/notifications — the /you page).
 *
 * `position: fixed` so the bar stays glued to the viewport bottom on
 * every page (sticky-bottom only triggers when the element is about to
 * scroll off-screen, which is the wrong semantic for a tab bar on a
 * long-scrolling page like the homepage).
 *
 * Visible at all widths in the mockup so reviewers can see the IA. In
 * the real build it would render only at mobile widths; desktop gets a
 * top nav bar instead.
 */

interface MobileTabBarProps {
  active: 'people' | 'calendar' | 'guides' | 'you';
  /** Notification count badge on the "You" tab — driven by
   *  `useYouTabBadge` (active alerts + the anon sign-in nudge). A number
   *  reads as "N notifications waiting"; the earlier bare red dot didn't
   *  communicate what it meant (Kate 2026-07-06). `true` is accepted for
   *  back-compat and renders as a countless dot. */
  youBadge?: number | boolean;
}

const Bar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 50;
  background: hsl(var(--card));
  border-top: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-left: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-right: 1px solid ${({ theme }) => theme.color.warmBorder};
  /* Tight bottom padding in a browser tab: Safari's own chrome sits below
     the bar and already clears the home indicator, so the safe-area inset
     here is just wasted vertical space (the gap below the icons). Re-add the
     inset only when Thea is installed to the home screen (standalone display
     mode), where there's no browser chrome to clear the indicator. */
  padding: 8px 0 4px;
  @media (display-mode: standalone) {
    padding-bottom: max(8px, env(safe-area-inset-bottom));
  }
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
  /* Desktop uses SiteHeader's right-grouped nav instead — hide the
   * bottom bar at ≥1024 to avoid double navigation. */
  @media (min-width: 1024px) {
    display: none;
  }
`;

/* THE-1232: the board UI standard's "2 weights, 2 text colors, clay is
 * brand only" rule reaches this bar too (it renders ON the board page, even
 * though the component lives in calendar/). Labels read theme.text.small
 * (one fifth size doesn't exist — this folds the former flat 11px literal
 * into the closest existing role; the bar is phone-only, so small.desktop
 * never actually renders) + theme.weight — semibold (not bold-bold 700)
 * when active, regular (not a second semibold) when inactive. The
 * SELECTED color is theme.color.foreground, not clay — clay is reserved
 * for brand surfaces (logo, Save band, the avatar), never a nav label; the
 * unselected color keeps reading mutedForeground.
 */
const TabLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  padding: 6px 4px;
  color: ${({ $active, theme }) =>
    $active ? theme.color.foreground : theme.color.mutedForeground};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.text.small.mobile};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.weight.semibold : theme.weight.regular};
  letter-spacing: 0.01em;
  /* Keep two-word labels (e.g. "Gift Guides") on a single line so the
   * four columns stay vertically aligned at narrow widths. */
  white-space: nowrap;
  &:hover {
    color: ${({ theme }) => theme.color.foreground};
  }
`;

/**
 * Inline SVG icons inherit `currentColor` from TabLink — so they pick up
 * the foreground (active) / muted-foreground (inactive) treatment
 * automatically without any extra styling. Replaces the previous emoji
 * icons which rendered blue/teal on Apple platforms (off-brand for Thea's
 * palette).
 */
const IconWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  line-height: 1;
`;

// Red count badge stacked on the You-tab icon — same red as the avatar's
// ThresholdDot, but carrying the number of waiting notifications so the
// signal is legible ("3" = three notifications, not just "something").
// With no count (boolean true) it collapses to the original bare dot.
const CountBadge = styled.span<{ $hasCount: boolean }>`
  position: absolute;
  top: -4px;
  right: ${({ $hasCount }) => ($hasCount ? '-7px' : '-2px')};
  min-width: ${({ $hasCount }) => ($hasCount ? '16px' : '9px')};
  height: ${({ $hasCount }) => ($hasCount ? '16px' : '9px')};
  padding: ${({ $hasCount }) => ($hasCount ? '0 4px' : '0')};
  border-radius: 9999px;
  background: #d93025;
  border: 2px solid hsl(var(--card));
  color: #ffffff;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
`;

const PeopleIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="7.5" r="3.25" />
    <path d="M2.5 20.5v-1.25a4 4 0 0 1 4-4h5a4 4 0 0 1 4 4v1.25" />
    <circle cx="17" cy="6.5" r="2.5" />
    <path d="M21.5 19.5v-1a3 3 0 0 0-2.5-2.96" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <line x1="3.5" y1="10" x2="20.5" y2="10" />
    <line x1="8" y1="3" x2="8" y2="6" />
    <line x1="16" y1="3" x2="16" y2="6" />
  </svg>
);

const GiftIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v12.5" />
    <path d="M19 12v6.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V12" />
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 5.5 12 8M16.5 8a2.5 2.5 0 0 0 0-5C14 3 12 5.5 12 8" />
  </svg>
);

const YouIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M4 20.5v-.5a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v.5" />
  </svg>
);

export const MobileTabBar: React.FC<MobileTabBarProps> = ({
  active,
  youBadge = false,
}) => {
  // Blocking bar: this is how the user navigates, so the consent banner lifts
  // above it rather than covering it.
  const barRef = useRef<HTMLElement>(null);
  useBottomBarInset(barRef);

  // H6 (THE-581). The hub is a TAB, so the natural second move for a preview
  // tester is Calendar and back — through THIS link. A bare `/gift-guides` here
  // drops `?giftGuideHub=1` and silently returns today's page mid-session,
  // which is the exact failure the param-carrying helper exists to prevent.
  // Resolves to the bare path for everyone without the param, so the DOM is
  // unchanged for normal traffic.
  const guidesHref = useGuidesIndexHref();

  // Normalize: number → count badge, boolean true → bare dot (no label).
  const badgeCount = typeof youBadge === 'number' ? youBadge : 0;
  const showBadge = typeof youBadge === 'number' ? youBadge > 0 : youBadge;
  const badgeLabel = badgeCount > 9 ? '9+' : badgeCount > 0 ? String(badgeCount) : '';
  const hubFrame = useHubFrame();
  if (hubFrame) return null;

  return (
    <Bar aria-label="Primary navigation" ref={barRef}>
      <TabLink
        to="/#your-people"
        $active={active === 'people'}
        aria-current={active === 'people' ? 'page' : undefined}
      >
        <IconWrap>
          <PeopleIcon />
        </IconWrap>
        <span>People</span>
      </TabLink>
      <TabLink
        to="/calendar"
        $active={active === 'calendar'}
        aria-current={active === 'calendar' ? 'page' : undefined}
      >
        <IconWrap>
          <CalendarIcon />
        </IconWrap>
        {/* "Calendar" everywhere (header/IA spec v5 PR 4 — Kate reversed
            the earlier "Events" naming; desktop nav already says Calendar). */}
        <span>Calendar</span>
      </TabLink>
      <TabLink
        to={guidesHref}
        $active={active === 'guides'}
        aria-current={active === 'guides' ? 'page' : undefined}
      >
        <IconWrap>
          <GiftIcon />
        </IconWrap>
        <span>Gift Guides</span>
      </TabLink>
      <TabLink
        to="/you"
        $active={active === 'you'}
        aria-current={active === 'you' ? 'page' : undefined}
      >
        <IconWrap>
          <YouIcon />
          {showBadge && (
            <CountBadge
              data-testid="you-tab-badge"
              aria-hidden="true"
              $hasCount={badgeLabel !== ''}
            >
              {badgeLabel}
            </CountBadge>
          )}
        </IconWrap>
        <span>You</span>
      </TabLink>
    </Bar>
  );
};

export default MobileTabBar;
